import type { InvestigationType } from "@validereinc/domain";
import {
  differenceInCalendarDays,
  isSameDay,
  startOfDay,
  subDays,
} from "date-fns";

export const InvestigationRecencyBucket = {
  TODAY: "today",
  YESTERDAY: "yesterday",
  LAST_7_DAYS: "last_7_days",
  LAST_30_DAYS: "last_30_days",
  OLDER: "older",
} as const;

export type InvestigationRecencyBucketType =
  (typeof InvestigationRecencyBucket)[keyof typeof InvestigationRecencyBucket];

const INVESTIGATION_RECENCY_BUCKET_ORDER: InvestigationRecencyBucketType[] = [
  InvestigationRecencyBucket.TODAY,
  InvestigationRecencyBucket.YESTERDAY,
  InvestigationRecencyBucket.LAST_7_DAYS,
  InvestigationRecencyBucket.LAST_30_DAYS,
  InvestigationRecencyBucket.OLDER,
];

export type InvestigationRecencyGroupType = {
  bucket: InvestigationRecencyBucketType;
  investigations: InvestigationType[];
};

export type InvestigationHistoryListRowType =
  | {
      type: "header";
      bucket: InvestigationRecencyBucketType;
      id: string;
    }
  | {
      type: "item";
      investigation: InvestigationType;
      id: string;
    };

const getRecencyBucket = (
  createdAt: string,
  referenceDate: Date
): InvestigationRecencyBucketType => {
  const createdDate = new Date(createdAt);

  if (isSameDay(createdDate, referenceDate)) {
    return InvestigationRecencyBucket.TODAY;
  }

  if (isSameDay(createdDate, subDays(referenceDate, 1))) {
    return InvestigationRecencyBucket.YESTERDAY;
  }

  const dayDifference = differenceInCalendarDays(
    startOfDay(referenceDate),
    startOfDay(createdDate)
  );

  if (dayDifference <= 7) {
    return InvestigationRecencyBucket.LAST_7_DAYS;
  }

  if (dayDifference <= 30) {
    return InvestigationRecencyBucket.LAST_30_DAYS;
  }

  return InvestigationRecencyBucket.OLDER;
};

/**
 * Groups investigations into recency buckets ordered newest-first within each
 * bucket and across buckets.
 */
export const groupInvestigationsByRecency = (
  investigations: InvestigationType[],
  referenceDate: Date = new Date()
): InvestigationRecencyGroupType[] => {
  const sortedInvestigations = [...investigations].sort(
    (leftInvestigation, rightInvestigation) =>
      new Date(rightInvestigation.created_at).getTime() -
      new Date(leftInvestigation.created_at).getTime()
  );

  const groupsByBucket = new Map<
    InvestigationRecencyBucketType,
    InvestigationType[]
  >();

  for (const investigation of sortedInvestigations) {
    const bucket = getRecencyBucket(investigation.created_at, referenceDate);
    const bucketInvestigations = groupsByBucket.get(bucket) ?? [];
    bucketInvestigations.push(investigation);
    groupsByBucket.set(bucket, bucketInvestigations);
  }

  return INVESTIGATION_RECENCY_BUCKET_ORDER.flatMap((bucket) => {
    const bucketInvestigations = groupsByBucket.get(bucket);

    if (!bucketInvestigations?.length) {
      return [];
    }

    return [{ bucket, investigations: bucketInvestigations }];
  });
};

/**
 * Flattens recency groups into virtualizer rows (section header + cards).
 */
export const flattenInvestigationRecencyGroupsToRows = (
  groups: InvestigationRecencyGroupType[]
): InvestigationHistoryListRowType[] =>
  groups.flatMap((group) => [
    {
      type: "header",
      bucket: group.bucket,
      id: `header-${group.bucket}`,
    },
    ...group.investigations.map((investigation) => ({
      type: "item" as const,
      investigation,
      id: investigation.id,
    })),
  ]);

/**
 * Returns virtualizer row indexes for recency section headers.
 */
export const getInvestigationHistoryStickyHeaderIndexes = (
  rows: InvestigationHistoryListRowType[]
): number[] =>
  rows.flatMap((row, index) => (row.type === "header" ? [index] : []));

/**
 * Returns the recency header that should stick while scrolling at `rangeStartIndex`.
 */
export const getActiveInvestigationHistoryStickyHeaderIndex = (
  stickyHeaderIndexes: number[],
  rangeStartIndex: number
): number => {
  if (stickyHeaderIndexes.length === 0) {
    return 0;
  }

  const activeStickyHeaderIndex = [...stickyHeaderIndexes]
    .reverse()
    .find((index) => rangeStartIndex >= index);

  return activeStickyHeaderIndex ?? stickyHeaderIndexes[0];
};
